import { useGetPage } from 'hooks/useGetPage';
import {
  GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS,
  pageIds,
  questionnairesIds,
} from 'utilities/constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { MyFormData } from 'auth/pages/SignUpProvider/useValidationSteps';
import { GenericStep } from 'components/stepper/types';
import QuestionnaireSection from './forms/QuestionnaireSection';
import Medication from './forms/Medication';
import { useQuestionnaireStepperForm } from 'components/stepper/useStepperForm';
import {
  FhirQuestionnaireNames,
  GetFhirQuestionnaireResponseDocument,
  GetFhirQuestionnaireResponseQuery,
  GetQuestionnaireOutput,
  useGetFhirQuestionnaireByIdQuery,
  useGetFhirQuestionnaireResponseQuery,
  useGetMedicationByHealthQuestionnairePatientCodexIdQuery,
  useInsertFhirQuestionnaireResponseMutation,
  useUpdateFhirQuestionnaireResponseMutation,
} from 'graphql/generated/remote-schema-hasura';
import { useAuth } from 'auth/context/AuthContext';
import {
  FileOutput,
  HasuraAnswerItem,
  MedicationRequestOutputDto,
  QuestionnaireResponseStatus,
  useUpdateSelfReportedMedicationMutation,
} from 'graphql/generated/hasura';
import { useCustomToast } from 'layout/useToast';
import { useNavigate } from 'react-router-dom';
import { MY_ACCOUNT_PROFILE } from 'utilities/routes';
import {
  arrayRequired,
  bloodPressureValidation,
  notInFutureDateTest,
  stringRequired,
} from 'utilities/validationSchemas/questionnaireValidation';
import { interpolateVariables } from 'utilities/functions';
import { I_DO_NOT_KNOW_VALUE } from './forms/BloodPressureField';
import { usePatientProfileContext } from '../patient/PatientProfileContext';
import { Roles } from '../../../../firebase/interfaces';
import moment from 'moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import { isValidNdc } from 'utilities/questionnaireHelpers';
import { DEFAULT_NONE_OPTION } from 'components/stepper/field-components/FormMultiSelect';
import { isJsonString } from '../../../my-appointments/components/appointment-details/useSummariForm';

const KIDNEY_STONES_ID = '850';
export const ALLERGENS_ID = '90';
export const MED_ALLERGENS_ID = '101';
export const BLOOD_PRESSURE_ID = '73';
export const ADDITIONAL_INFORMATION_ID = '92';

const ADDITIONAL_TEXT_KEY = `${ADDITIONAL_INFORMATION_ID}_text` as const;
const ADDITIONAL_FILE_KEY = `${ADDITIONAL_INFORMATION_ID}_file` as const;
export const ADDITIONAL_FILES_KEY =
  `${ADDITIONAL_INFORMATION_ID}_files` as const;

export type AdditionalOutputType = {
  [ADDITIONAL_TEXT_KEY]: string;
  [ADDITIONAL_FILE_KEY]: string;
  [ADDITIONAL_FILES_KEY]: FileOutput[];
};

const BLOOD_PRESSURE_DEFAULT_VALUE = ['', ''] as [string, string];

export type SavedMedicationOutput = Pick<
  MedicationRequestOutputDto,
  | 'note'
  | 'frequency'
  | 'dosage'
  | 'duration'
  | 'ndc'
  | 'displayName'
  | 'endDate'
  | 'effectiveDate'
>;

type PatientHealthKey =
  | '57'
  | '58'
  | '59'
  | '60'
  | '61'
  | '62'
  | '63'
  | '64'
  | '65'
  | '66'
  | '69'
  | '70'
  | '71'
  | '72'
  | '73'
  | '74'
  | '85'
  | '90'
  | '91'
  | '92'
  | '850';

type ChildHealthMap = Record<PatientHealthKey, string | Array<string>>;

const MEDICATION_IDS = {
  name: '75',
  dose: '79',
  time: '81',
  notes: '84',
  medicationList: '99',
};

export type Medication = {
  name: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  dose: string;
  units: string;
  frequency: {
    count: string;
    time: string;
  };
  dates?: {
    start: string;
    end: string;
  };
  notes: string;
  isSaved: boolean;
  regularly?: boolean;
};
export type MedAllergens = {
  label: string;
  medication: string;
  reaction: string[];
};

export type ChildHealthDataType = ChildHealthMap & {
  medication: Medication[];
  medAllergens: MedAllergens[];
  medicationOld: Record<string, unknown>[];
  [BLOOD_PRESSURE_ID]: BloodPressureFormValue;
  allergens: string[];
  [MED_ALLERGENS_ID]: string[];
};

export type BloodPressureFormValue = {
  idk: boolean;
  value: [string, string];
};

let insertionLockForUser: string | null = null;

export const useHealthQuestionnaireValidations = () => {
  const { user: loggedUser } = useAuth();
  const { addToast } = useCustomToast();
  const navigate = useNavigate();
  const { data } = useGetPage({
    locale: 'en',
    pageId: pageIds.MY_PROFILE,
  });

  const { data: savedMedications, loading: savedMedicationLoading } =
    useGetMedicationByHealthQuestionnairePatientCodexIdQuery({
      variables: {
        patientCodexId: loggedUser?.uuid ?? '',
      },
    });

  const [checkedResponse, setCheckedResponse] = useState(false);
  const isFormPopulated = useRef(false);

  const { data: questionneire } = useGetFhirQuestionnaireByIdQuery({
    fetchPolicy: 'network-only',
    variables: {
      questionnaire_id: questionnairesIds.HEALTH_QUESTIONNAIRE,
    },
  });

  const {
    data: savedQuestionaire,
    loading: responseLoading,
    error,
    refetch,
  } = useGetFhirQuestionnaireResponseQuery({
    fetchPolicy: 'network-only',
    context: {
      ignoreGlobalErrors: true,
    },
    skip: !loggedUser?.uuid || loggedUser?.role !== Roles.PATIENT,
    variables: {
      codexPatientId: loggedUser?.uuid || '',
      identifier: FhirQuestionnaireNames.HealthQuestionnaire,
      system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
    },
    onCompleted: () => {
      setCheckedResponse(true);
    },
    onError: () => {
      setCheckedResponse(true);
    },
  });

  const [updateMedications] = useUpdateSelfReportedMedicationMutation({
    refetchQueries: ['GetMedicationByHealthQuestionnairePatientCodexId'],
    onError: (error) => addToast({ message: error?.message, type: 'warning' }),
  });

  const isComplete = useMemo(
    () =>
      savedQuestionaire?.getFHIRQuestionnaireResponse?.questionnaire_response
        ?.is_complete === QuestionnaireResponseStatus.Completed,
    [savedQuestionaire],
  );

  const [
    insertFhirQuestionnaireResponses,
    { loading: isSavingFhirQuestionnaireResponses },
  ] = useInsertFhirQuestionnaireResponseMutation({
    refetchQueries: [
      {
        query: GetFhirQuestionnaireResponseDocument,
        variables: {
          codexPatientId: loggedUser?.uuid || '',
          identifier: FhirQuestionnaireNames.HealthQuestionnaire,
          system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
        },
      },
    ],
  });

  const [updateFhirQuestionnaireResponses, { loading: submitingStep }] =
    useUpdateFhirQuestionnaireResponseMutation({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetFhirQuestionnaireResponseDocument,
          variables: {
            codexPatientId: loggedUser?.uuid || '',
            identifier: FhirQuestionnaireNames.HealthQuestionnaire,
            system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
          },
        },
      ],
      onCompleted: () => {
        console.log('updated');
      },
    });

  const { userValidation, updatePatientValidation } =
    usePatientProfileContext();

  const getInitialQuestionerData = useCallback(
    (savedQuestionaire?: GetFhirQuestionnaireResponseQuery) => {
      if (
        !savedQuestionaire ||
        !savedQuestionaire.getFHIRQuestionnaireResponse
      ) {
        return {};
      }
      const data =
        savedQuestionaire.getFHIRQuestionnaireResponse.questionnaire_response
          .responses ?? [];

      const medicationResponse =
        savedQuestionaire.getFHIRQuestionnaireResponse.questionnaire_response.responses.find(
          (questionnaire: Record<string, unknown>) =>
            questionnaire?.questionID?.toString() ===
            MEDICATION_IDS.medicationList,
        )?.hasuraItemArrayResponse ?? [];

      const medicationOld = medicationResponse.map((med: HasuraAnswerItem) => {
        const [name, dose, unit, count, time, notes] =
          med?.hasuraItemArrayResponse ?? [];
        return {
          name: name?.stringResponse ?? '',
          dose: dose?.hasuraMedicationItemResponse?.value,
          units: unit?.hasuraMedicationItemResponse?.value,
          frequency: {
            count: count?.hasuraMedicationItemResponse?.value,
            time: time?.hasuraMedicationItemResponse?.value,
          },
          notes: notes?.stringResponse ?? '',
          isSaved: true,
        };
      });

      const medication = (
        savedMedications?.getFHIRMedicationRequestByPatientCodexId
          ?.medication_requests ?? []
      ).map((med: SavedMedicationOutput) => {
        const {
          ndc,
          duration,
          frequency,
          note,
          dosage,
          displayName,
          effectiveDate,
          endDate,
        } = med;
        const label = `${displayName} ${dosage}`;
        return {
          label: label,
          name: ndc,
          frequency: {
            count: frequency,
            time: duration,
          },
          startDate: effectiveDate
            ? moment(effectiveDate).format(STANDARD_DATE)
            : undefined,
          endDate: endDate ? moment(endDate).format(STANDARD_DATE) : '',
          regularly: !endDate,
          notes: note,
          isSaved: true,
        };
      });

      const additionalInfoResponse =
        savedQuestionaire.getFHIRQuestionnaireResponse.questionnaire_response.responses.find(
          (questionnaire: Record<string, unknown>) =>
            questionnaire?.questionID?.toString() === ADDITIONAL_INFORMATION_ID,
        )?.stringResponse;

      const additionalInfo = isJsonString(additionalInfoResponse ?? '')
        ? JSON.parse(additionalInfoResponse ?? '')
        : undefined;

      const kidneyStones = data.reduce(
        (acc, questionnaire: Record<string, unknown>) => {
          const questionID = questionnaire?.questionID as string;
          if (questionID.toString() !== KIDNEY_STONES_ID) {
            return acc;
          }

          const formatedResponse = (
            questionnaire.hasuraItemArrayResponse as Record<string, unknown>[]
          )?.reduce((acc, question) => {
            const questionID = question?.questionID as string;
            acc[questionID] = question?.stringResponse ?? '';
            return acc;
          }, {} as Record<string, unknown>);

          return [...acc, formatedResponse];
        },
        [] as unknown[],
      );

      const [medAllergens] = data.reduce(
        (acc, questionnaire: Record<string, unknown>) => {
          const questionID = questionnaire?.questionID as string;
          if (questionID.toString() !== MED_ALLERGENS_ID) {
            return acc;
          }

          const formatedResponse = (
            questionnaire.stringArrayResponse as string[]
          )
            ?.map((question) => JSON.parse(question))
            ?.filter(({ value }) => !isValidNdc(value));
          const medAllergens = formatedResponse?.length
            ? formatedResponse.map(({ value, label, reaction }) => ({
                label,
                medication: value,
                reaction,
              }))
            : [
                {
                  label: '',
                  medication: '',
                  reaction: [],
                },
              ];

          return [...acc, medAllergens];
        },
        [] as unknown[][],
      );

      const bloodPressureResponse = data.reduce<BloodPressureFormValue>(
        (acc, questionnaire) => {
          const q = questionnaire as Record<string, unknown>;
          const questionID = String(q.questionID ?? '');
          if (questionID !== BLOOD_PRESSURE_ID) return acc;

          const stringResponse =
            (q.stringResponse as string | null | undefined) ?? null;
          const idk = stringResponse === I_DO_NOT_KNOW_VALUE;

          const arr = q.stringArrayResponse as unknown;

          const value: [string, string] = Array.isArray(arr)
            ? [String(arr[0] ?? ''), String(arr[1] ?? '')]
            : BLOOD_PRESSURE_DEFAULT_VALUE;

          return {
            idk,
            value: idk ? BLOOD_PRESSURE_DEFAULT_VALUE : value,
          };
        },
        { idk: false, value: BLOOD_PRESSURE_DEFAULT_VALUE },
      );

      return {
        ...data.reduce((acc, questionnaire: Record<string, unknown>) => {
          const questionID = questionnaire?.questionID as string;
          if (questionID.toString() === MEDICATION_IDS.medicationList) {
            return acc;
          }
          return {
            ...acc,
            [questionID]:
              questionnaire?.stringResponse ||
              questionnaire.stringArrayResponse,
          };
        }, {}),
        medication,
        medicationOld,
        medAllergens: medAllergens?.length
          ? medAllergens
          : [
              {
                label: '',
                medication: '',
                reaction: [],
              },
            ],
        [ADDITIONAL_TEXT_KEY]: additionalInfo?.detail,
        [ADDITIONAL_FILES_KEY]: additionalInfo?.files,
        [KIDNEY_STONES_ID]: kidneyStones.length
          ? kidneyStones
          : [{ '86': '', '87': '', '88': '' }],
        [BLOOD_PRESSURE_ID]: bloodPressureResponse,
      };
    },
    [savedMedications],
  );

  const locale = useMemo(
    () => data.validateErrors || { required: 'Field is required' },
    [data],
  );

  const generalValidation = yup.object<Partial<MyFormData>>().shape({
    '57': stringRequired(locale),
    '58': stringRequired(locale),
    '59': stringRequired(locale),
    '60': stringRequired(locale),
    '61': stringRequired(locale),
    '62': stringRequired(locale),
    '63': stringRequired(locale),
    '64': stringRequired(locale),
    '65': stringRequired(locale),
    '66': stringRequired(locale),
  });

  const requiredRow = yup.object({
    '86': yup
      .string()
      .trim()
      .test(
        'not-in-future',
        locale.invalidFutureDate || 'Date cannot be in the future.',
        notInFutureDateTest,
      )
      .required('Field is required'),
    '87': yup.string().trim().required('Field is required'),
    '88': yup.string().trim().required('Field is required'),
  });

  const healthSectionValidation = yup.object().shape({
    [ALLERGENS_ID]: arrayRequired(locale),
    '69': stringRequired(locale),
    '70': stringRequired(locale),
    '71': arrayRequired(locale),
    '72': arrayRequired(locale),
    '73': bloodPressureValidation,
    '74': stringRequired(locale),
    '85': stringRequired(locale),
    '91': stringRequired(locale),
    medAllergens: yup.array().of(
      yup.object({
        medication: yup.string().required(locale?.required),
        label: yup.string().required(locale?.required),
        reaction: yup
          .array()
          .of(yup.string())
          .when('medication', {
            is: (val: string) => val !== DEFAULT_NONE_OPTION,
            then: (schema) =>
              schema.min(1, locale?.required).required(locale?.required),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    ),
    [KIDNEY_STONES_ID]: yup.mixed().when(['85'], ([v]) => {
      if (v === 'Yes') {
        return yup.array().of(requiredRow).min(1, 'Add at least one record');
      }
      return yup.mixed().notRequired().nullable();
    }),
  });

  const medication = yup.object<Partial<MyFormData>>().shape({
    medication: yup.array().of(
      yup.object({
        name: yup.string().required(locale?.required),
        regularly: yup.boolean(),
        startDate: yup
          .date()
          .required(locale?.required)
          .typeError(locale?.required),
        endDate: yup
          .date()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          )
          .nullable()
          .when('regularly', {
            is: false,
            then: (schema) =>
              schema
                .typeError(locale?.required)
                .required(locale?.required)
                .min(
                  yup.ref('startDate'),
                  locale?.invalidDate || 'End date cannot be before start date',
                ),
            otherwise: (schema) => schema.notRequired(),
          }),
        notes: yup.string().nullable(),
        frequency: yup
          .object({
            count: yup.string().required(locale?.required),
            time: yup.string().required(locale?.required),
          })
          .required(locale?.required),
      }),
    ),
  });

  const GeneralInfoComponent = useCallback(
    () =>
      QuestionnaireSection({
        sectionIndex: 0,
        getQuestionnaireOutput:
          questionneire?.getFHIRQuestionnaireByCodexID as GetQuestionnaireOutput,
      }),
    [questionneire],
  );

  const HealthHistoryComponent = useCallback(
    () =>
      QuestionnaireSection({
        sectionIndex: 1,
        getQuestionnaireOutput:
          questionneire?.getFHIRQuestionnaireByCodexID as GetQuestionnaireOutput,
      }),
    [questionneire],
  );

  const MedicationComponent = useCallback(
    () =>
      Medication({
        sectionIndex: 2,
        getQuestionnaireOutput:
          questionneire?.getFHIRQuestionnaireByCodexID as GetQuestionnaireOutput,
      }),
    [questionneire],
  );

  const steps: GenericStep[] = [
    {
      id: 'generali-info',
      name:
        questionneire?.getFHIRQuestionnaireByCodexID?.data?.[0].name ??
        'Generali Info',
      schema: generalValidation,
      component: GeneralInfoComponent,
    },
    {
      id: 'health-history',
      name:
        questionneire?.getFHIRQuestionnaireByCodexID?.data?.[1].name ??
        'Generali Info',
      schema: healthSectionValidation,
      component: HealthHistoryComponent,
    },
    {
      id: 'medications',
      name:
        questionneire?.getFHIRQuestionnaireByCodexID?.data?.[2].name ??
        'Medications',
      schema: medication,
      component: MedicationComponent,
    },
  ];

  const insertRef = useRef(false);

  const { currentStep, methods, handleNextStep, onError } =
    useQuestionnaireStepperForm<ChildHealthDataType & AdditionalOutputType>({
      steps,
      mode: 'onTouched',
      searchLastSavedStep: false,
      initialValues: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [KIDNEY_STONES_ID]: [{ '86': '', '87': '', '88': '' }],
        [ALLERGENS_ID]: [],
        medAllergens: [
          {
            label: '',
            medication: '',
            reaction: [],
          },
        ],
        [BLOOD_PRESSURE_ID]: {
          idk: false,
          value: BLOOD_PRESSURE_DEFAULT_VALUE,
        },
      },
    });

  const saveData = async (
    data: ChildHealthDataType & AdditionalOutputType,
    isLastStep = true,
  ) => {
    const {
      medication,
      medicationOld,
      [KIDNEY_STONES_ID]: kidneyStones,
      [BLOOD_PRESSURE_ID]: bloodPressure,
      [ADDITIONAL_TEXT_KEY]: additionalText,
      [ADDITIONAL_FILES_KEY]: additionalFiles,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [ADDITIONAL_FILE_KEY]: remove,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [ADDITIONAL_INFORMATION_ID]: removePrev,
      medAllergens = [
        {
          label: '',
          medication: '',
          reaction: [],
        },
      ],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [MED_ALLERGENS_ID]: allergensPressure,
      ...rest
    } = data;

    const request = Object.entries(rest)
      .filter(([, value]) => !!value)
      .map(([key, value]) => ({
        questionID: key,
        response: value,
        currentSection:
          questionneire?.getFHIRQuestionnaireByCodexID?.data?.find(
            ({ properties }) => properties.includes(key),
          )?.name,
      }));

    const medicationRequest = ((medicationOld as Medication[]) ?? []).map(
      ({ name, dose, isSaved, notes, frequency, units }, index) => ({
        completed: isSaved,
        id: index,
        title: `Medication list ${index + 1}`,
        answers: [
          {
            questionID: MEDICATION_IDS.name,
            response: name,
          },
          {
            questionID: MEDICATION_IDS.dose,
            response: { id: 'answer-1', value: dose ?? '' },
          },
          {
            questionID: MEDICATION_IDS.dose,
            response: { id: 'answer-2', value: units ?? '' },
          },
          {
            questionID: MEDICATION_IDS.time,
            response: { id: 'answer-1', value: frequency.count ?? '' },
          },
          {
            questionID: MEDICATION_IDS.time,
            response: { id: 'answer-2', value: frequency.time ?? '' },
          },
          {
            questionID: MEDICATION_IDS.notes,
            response: notes,
          },
        ],
      }),
    );

    const bloodPressureValue = (
      bloodPressure as unknown as BloodPressureFormValue
    ).idk
      ? I_DO_NOT_KNOW_VALUE
      : (bloodPressure as unknown as BloodPressureFormValue).value;

    const allergensValue = medAllergens.map(({ label, reaction, medication }) =>
      JSON.stringify({ label: label, value: medication, reaction }),
    );

    const bloodPressureRequest = {
      questionID: BLOOD_PRESSURE_ID,
      currentSection: 'Health History',
      response: bloodPressureValue,
    };

    const allergensPressureRequest = {
      questionID: MED_ALLERGENS_ID,
      currentSection: 'Health History',
      response: allergensValue,
    };

    const additionalRequest = {
      questionID: ADDITIONAL_INFORMATION_ID,
      currentSection: 'Health History',
      response: JSON.stringify({
        detail: additionalText,
        files: additionalFiles,
      }),
    };

    const kidneyStonesRequest = (
      kidneyStones as unknown as Record<string, string>[]
    ).map((item) => ({
      questionID: KIDNEY_STONES_ID,
      currentSection: 'Health History',
      response:
        Object.entries(item || {}).map(([key, value]) => ({
          questionID: key,
          response: value,
        })) || {},
    }));

    if (isLastStep) {
      const medRequest = medication.map(
        ({
          name,
          notes,
          frequency,
          startDate,
          endDate,
          regularly = false,
        }) => ({
          ndc: name,
          note: notes,
          duration: frequency?.time,
          frequency: frequency.count,
          startDate: moment(startDate).format(STANDARD_DATE),
          endDate: !regularly
            ? moment(endDate).format(STANDARD_DATE)
            : undefined,
        }),
      );
      const { data } = await updateMedications({
        variables: {
          medications: medRequest,
        },
      });

      if (!data) {
        throw new Error('Medications not added');
      }
    }

    return updateFhirQuestionnaireResponses({
      variables: {
        questionnaireId: '',
        identifier: FhirQuestionnaireNames.HealthQuestionnaire,
        responses: [
          bloodPressureRequest,
          allergensPressureRequest,
          additionalRequest,
          ...kidneyStonesRequest,
          ...request,
          {
            currentSection: 'Medications',
            questionID: MEDICATION_IDS.medicationList,
            response: medicationRequest,
          },
        ],
        swabCode: '',
        isComplete: isComplete || isLastStep,
      },
      onCompleted: () => {
        addToast({
          type: 'success',
          message: interpolateVariables(
            'Step {{step}}/{{stepsTotal}} of health questionnaire is updated successfully',
            {
              step: (currentStep + 1)?.toString(),
              stepsTotal: steps.length?.toString(),
            },
          ),
        });

        if (!isLastStep) return;

        if (!userValidation.isQuestionnaireValid) {
          updatePatientValidation();
        }

        navigate(`${MY_ACCOUNT_PROFILE}/profile`);
      },
    });
  };
  const handleSaveStep = async (
    data: ChildHealthDataType & AdditionalOutputType,
    currentStep: number,
  ) => {
    return methods.handleSubmit(
      () =>
        saveData(data, currentStep >= steps.length - 2)
          .then(() => (isFormPopulated.current = false))
          .then(() => handleNextStep())
          .catch(console.warn),
      onError,
    )();
  };
  const onSubmit = (data: ChildHealthDataType & AdditionalOutputType) => {
    saveData(data, true);
  };

  useEffect(() => {
    if (
      !isFormPopulated.current &&
      !savedMedicationLoading &&
      savedQuestionaire?.getFHIRQuestionnaireResponse
    ) {
      const fetchedData = getInitialQuestionerData(savedQuestionaire);
      const finalInitialValues = {
        ...fetchedData,
      };

      methods.reset(finalInitialValues);
      isFormPopulated.current = true;
    }
  }, [
    savedQuestionaire,
    getInitialQuestionerData,
    methods,
    savedMedicationLoading,
  ]);

  useEffect(() => {
    if (
      checkedResponse &&
      !responseLoading &&
      !savedQuestionaire?.getFHIRQuestionnaireResponse.questionnaire_response
        .id &&
      loggedUser?.uuid &&
      !insertionLockForUser &&
      !insertRef.current
    ) {
      insertRef.current = true;
      insertionLockForUser = loggedUser.uuid;

      insertFhirQuestionnaireResponses({
        variables: {
          createQuestionnaireResponsesInput: {
            codexPatientId: loggedUser?.uuid,
            questionnaireName: FhirQuestionnaireNames.HealthQuestionnaire,
            questionnaireResponse: [],
            serviceRequestId: '',
            completed: false,
          },
        },
        onCompleted: () => {
          refetch();
        },
      });
    }
  }, [
    insertFhirQuestionnaireResponses,
    loggedUser?.uuid,
    refetch,
    error,
    responseLoading,
    savedQuestionaire?.getFHIRQuestionnaireResponse.questionnaire_response.id,
    checkedResponse,
  ]);

  return {
    currentStep,
    methods,
    handleNextStep,
    handleSaveStep,
    steps,
    onSubmit,
    onError,
    submitingStep,
    submited: isSavingFhirQuestionnaireResponses,
    totalSteps: steps.length,
  };
};
